import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { updateMyAnnonce } from "@/lib/api";
import { ABIDJAN_QUARTIERS, ENERGIES, BOITES, VEHICULE_MARQUES, MOTO_MARQUES, CYLINDREES, isMoto } from "@/lib/utils";
import { useCountry } from "@/lib/CountryContext";

export default function EditMyAnnonceModal({ annonce, open, onClose, onSaved }) {
  const { country } = useCountry();
  const [title, setTitle] = useState(annonce?.title || "");
  const [price, setPrice] = useState(annonce?.price ?? "");
  const [priceUnit, setPriceUnit] = useState(annonce?.price_unit || "");
  const [city, setCity] = useState(annonce?.city || "");
  const [quartier, setQuartier] = useState(annonce?.quartier || "");
  const [description, setDescription] = useState(annonce?.description || "");
  const [whatsapp, setWhatsapp] = useState(annonce?.whatsapp || "");
  // Phase 2 Immobilier
  const [typeBien, setTypeBien] = useState(annonce?.type_bien || "");
  const [statut, setStatut] = useState(annonce?.statut || "");
  const [etatBien, setEtatBien] = useState(annonce?.etat_bien || "");
  const [docsTerrain, setDocsTerrain] = useState(annonce?.documents_terrain || "");
  const [usageTerrain, setUsageTerrain] = useState(annonce?.usage_terrain || "");
  const [surfaceM2, setSurfaceM2] = useState(annonce?.surface_m2 || "");
  const [energie, setEnergie] = useState(annonce?.energie || "");
  const [marque, setMarque] = useState(annonce?.marque || "");
  const [annee, setAnnee] = useState(annonce?.annee ?? "");
  const [kilometrage, setKilometrage] = useState(annonce?.kilometrage ?? "");
  const [boite, setBoite] = useState(annonce?.boite || "");
  const [cylindree, setCylindree] = useState(annonce?.cylindree || "");
  const [loading, setLoading] = useState(false);

  if (!annonce) return null;
  const isImmo = annonce?.category === "Immobilier";
  const isVehicule = annonce?.category === "Vehicules";

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        title: title.trim(),
        price: Number(price),
        price_unit: priceUnit,
        city: city.trim(),
        quartier: quartier.trim(),
        description: description.trim(),
        whatsapp: whatsapp.trim(),
      };
      if (isImmo) {
        payload.type_bien = typeBien;
        payload.statut = statut;
        payload.etat_bien = etatBien;
        payload.documents_terrain = docsTerrain;
        payload.usage_terrain = usageTerrain;
        payload.surface_m2 = surfaceM2 ? Number(surfaceM2) : null;
      }
      if (isVehicule) {
        payload.energie = energie;
        payload.marque = marque;
        payload.annee = annee === "" ? null : Number(annee);
        payload.kilometrage = kilometrage === "" ? null : Number(kilometrage);
        payload.boite = boite;
        if (isMoto(annonce?.subcategory)) payload.cylindree = cylindree;
      }
      const updated = await updateMyAnnonce(annonce.id, payload);
      toast.success("Annonce mise a jour");
      onSaved?.(updated);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Echec de la mise a jour";
      toast.error(typeof msg === "string" ? msg : "Echec de la mise a jour");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-3xl" data-testid="edit-my-annonce-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Modifier mon annonce</DialogTitle>
          <DialogDescription className="text-sm text-[var(--ink-soft)]">
            Vous pouvez ajuster le prix, la description et la localisation. La categorie ne peut pas etre modifiee.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-3 mt-2" data-testid="edit-my-annonce-form">
          <Field label="Titre">
            <input value={title} onChange={(e) => setTitle(e.target.value)} required className={inputCls} data-testid="edit-title-input" />
          </Field>
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <Field label="Prix (FCFA)">
              <input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} required className={inputCls} data-testid="edit-price-input" />
            </Field>
            <Field label="Unite">
              <select value={priceUnit} onChange={(e) => setPriceUnit(e.target.value)} className={inputCls} data-testid="edit-price-unit">
                <option value="">Fixe</option>
                <option value="/ nuit">/ nuit</option>
                <option value="/ mois">/ mois</option>
                <option value="/ an">/ an</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ville">
              <select value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} data-testid="edit-city-input">
                <option value="">--</option>
                {country.cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Quartier">
              {city === "Abidjan" ? (
                <select value={quartier} onChange={(e) => setQuartier(e.target.value)} className={inputCls} data-testid="edit-quartier-input">
                  <option value="">--</option>
                  {ABIDJAN_QUARTIERS.map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              ) : (
                <input value={quartier} onChange={(e) => setQuartier(e.target.value)} className={inputCls} data-testid="edit-quartier-input" placeholder="Quartier" />
              )}
            </Field>
          </div>
          <Field label="WhatsApp (avec indicatif)">
            <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} className={inputCls} data-testid="edit-whatsapp-input" placeholder="+225 0X XX XX XX XX" />
          </Field>
          <Field label="Description">
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} className={inputCls} data-testid="edit-description-input" />
          </Field>

          {isVehicule && (
            <div className="p-3 bg-orange-50/60 border border-orange-200 rounded-2xl space-y-3" data-testid="edit-vehicule-section">
              <Field label="Marque">
                <select value={marque} onChange={(e) => setMarque(e.target.value)} className={inputCls} data-testid="edit-marque">
                  <option value="">--</option>
                  {(isMoto(annonce?.subcategory) ? MOTO_MARQUES : VEHICULE_MARQUES).map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Type d'energie">
                <select value={energie} onChange={(e) => setEnergie(e.target.value)} className={inputCls} data-testid="edit-energie">
                  <option value="">--</option>
                  {ENERGIES.map((en) => <option key={en.key} value={en.key}>{en.label}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Annee">
                  <input type="number" min="1980" value={annee} onChange={(e) => setAnnee(e.target.value)} className={inputCls} data-testid="edit-annee" />
                </Field>
                <Field label="Kilometrage">
                  <input type="number" min="0" value={kilometrage} onChange={(e) => setKilometrage(e.target.value)} className={inputCls} data-testid="edit-kilometrage" />
                </Field>
              </div>
              <Field label="Boite de vitesses">
                <select value={boite} onChange={(e) => setBoite(e.target.value)} className={inputCls} data-testid="edit-boite">
                  <option value="">--</option>
                  {BOITES.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                </select>
              </Field>
              {isMoto(annonce?.subcategory) && (
                <Field label="Cylindree">
                  <select value={cylindree} onChange={(e) => setCylindree(e.target.value)} className={inputCls} data-testid="edit-cylindree">
                    <option value="">--</option>
                    {CYLINDREES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </Field>
              )}
            </div>
          )}

          {isImmo && (
            <div className="p-3 bg-orange-50/60 border border-orange-200 rounded-2xl space-y-3" data-testid="edit-immo-section">
              <div className="text-xs font-bold uppercase text-orange-700 tracking-wide">Classification Immobilier</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type de bien">
                  <select value={typeBien} onChange={(e) => setTypeBien(e.target.value)} className={inputCls} data-testid="edit-type-bien">
                    <option value="">--</option>
                    <option value="maison">Maison / Villa</option>
                    <option value="appartement">Appartement / Studio</option>
                    <option value="terrain">Terrain</option>
                  </select>
                </Field>
                <Field label="Statut">
                  <select value={statut} onChange={(e) => setStatut(e.target.value)} className={inputCls} data-testid="edit-statut">
                    <option value="">--</option>
                    <option value="vente">A vendre</option>
                    <option value="location">A louer</option>
                  </select>
                </Field>
              </div>
              {typeBien && typeBien !== "terrain" && (
                <Field label="Etat du bien">
                  <select value={etatBien} onChange={(e) => setEtatBien(e.target.value)} className={inputCls} data-testid="edit-etat-bien">
                    <option value="">--</option>
                    <option value="sans_travaux">Sans travaux</option>
                    <option value="avec_travaux">Avec travaux</option>
                    <option value="inachevee">Inachevee</option>
                  </select>
                </Field>
              )}
              {typeBien === "terrain" && statut === "vente" && (
                <Field label="Documents fonciers">
                  <select value={docsTerrain} onChange={(e) => setDocsTerrain(e.target.value)} className={inputCls} data-testid="edit-documents-terrain">
                    <option value="">--</option>
                    <option value="avec_document">Avec documents</option>
                    <option value="sans_document">Sans documents</option>
                  </select>
                </Field>
              )}
              {typeBien === "terrain" && statut === "location" && (
                <Field label="Usage du terrain">
                  <select value={usageTerrain} onChange={(e) => setUsageTerrain(e.target.value)} className={inputCls} data-testid="edit-usage-terrain">
                    <option value="">--</option>
                    <option value="ville">Terrain de ville</option>
                    <option value="agricole">Terrain agricole</option>
                  </select>
                </Field>
              )}
              <Field label="Surface (m²)">
                <input
                  type="number" min="0" value={surfaceM2}
                  onChange={(e) => setSurfaceM2(e.target.value)}
                  className={inputCls} data-testid="edit-surface-m2"
                  placeholder="350"
                />
              </Field>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="edit-cancel-btn">Annuler</button>
            <button type="submit" disabled={loading} className="btn-primary inline-flex items-center gap-2" data-testid="edit-save-btn">
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[var(--ink-soft)] block mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full !py-2.5 !px-3 rounded-xl border border-[var(--line)] focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition bg-white";
